import { Language } from 'assets/locales/Locale';
import { createPathFromRoute } from 'assets/utils/dom/UrlParsing';
import executeServerApi from 'assets/utils/server/ServerApi';
import { getDefaultHeaders, HttpMethod } from 'config/Api.Config';
import { omit } from 'lodash';
import MediaFile from 'models/core/mediaFile/Model.MediaFile';
import ProductEnhancementDealerDocument from 'models/dealerManagement/productEnhancementDealerDocument/Model.ProductEnhancementDealerDocument';
import Model from 'models/Model';
import ProductEnhancementAdditionalField from '../productEnhancementAdditionalField/Model.ProductEnhancementAdditionalField';
import productEnhancementDocumentFindApi from './find/Api.ProductEnhancementDocument.Find';

export async function productEnhancementDocumentSaveApi(body: Api.Core.ProductEnhancementDocument.Save.IRequest) {
  const serverResponse = await executeServerApi<
    Api.Core.ProductEnhancementDocument.Save.IRequest,
    Api.Core.ProductEnhancementDocument.Save.IResponse,
    Api.Core.ProductEnhancementDocument.Save.IHeaders
  >({
    method: HttpMethod.post,
    url: createPathFromRoute('/api/ProductManagement/ProductEnhancement/:id/Documents', {
      id: body.productEnhancementId,
    }),
    body: {
      ...omit(body, 'productEnhancementAdditionalFields'),
      fillablePdfFields: body.productEnhancementAdditionalFields,
    } as any,
    headers: getDefaultHeaders(),
  });
  return serverResponse;
}
export async function productEnhancementDocumentUpdateAllFieldsApi(
  body: Api.Core.ProductEnhancementDocument.UpdateAll.IRequest
) {
  const serverResponse = await executeServerApi<
    Omit<Api.Core.ProductEnhancementDocument.UpdateAll.IRequest, 'productEnhancementId'>,
    Api.Core.ProductEnhancementDocument.UpdateAll.IResponse,
    Api.Core.ProductEnhancementDocument.UpdateAll.IHeaders
  >({
    method: HttpMethod.put,
    url: createPathFromRoute('/api/ProductManagement/ProductEnhancement/:id/Documents', {
      id: body.productEnhancementId,
    }),
    body: {
      ...omit(body, 'productEnhancementAdditionalFields', 'productEnhancementId'),
      fillablePdfFields: body.productEnhancementAdditionalFields,
    } as any,
    headers: getDefaultHeaders(),
  });
  return serverResponse;
}
export async function productEnhancementDocumentDeleteApi(params: Api.Core.ProductEnhancementDocument.Delete.IRequest) {
  const serverResponse = await executeServerApi<
    Api.Core.ProductEnhancementDocument.Delete.IRequest,
    Api.Core.ProductEnhancementDocument.Delete.IResponse,
    Api.Core.ProductEnhancementDocument.Delete.IHeaders
  >({
    method: HttpMethod.delete,
    url: createPathFromRoute('/api/ProductManagement/ProductEnhancement/:id/Documents/:documentId', params),
    headers: getDefaultHeaders(),
  });
  return serverResponse;
}
export async function productEnhancementDocumentReplaceApi(
  params: Api.Core.ProductEnhancementDocument.Replace.IRequest
) {
  const serverResponse = await executeServerApi<
    Pick<Api.Core.ProductEnhancementDocument.Replace.IRequest, 'formFile'>,
    Api.Core.ProductEnhancementDocument.Replace.IResponse,
    Api.Core.ProductEnhancementDocument.Replace.IHeaders
  >({
    method: HttpMethod.post,
    url: createPathFromRoute('/api/ProductManagement/ProductEnhancement/:id/Documents/:documentId/ReplaceFile', {
      id: params.id,
      documentId: params.documentId,
    }),
    body: { formFile: params.formFile },
    headers: getDefaultHeaders(),
  });
  return serverResponse;
}
export async function productEnhancementDocumentOrderApi(params: Api.Core.ProductEnhancementDocument.Order.IRequest) {
  const serverResponse = await executeServerApi<
    Api.Core.ProductEnhancementDocument.Order.IRequest,
    Api.Core.ProductEnhancementDocument.Order.IResponse,
    Api.Core.ProductEnhancementDocument.Order.IHeaders
  >({
    method: HttpMethod.post,
    url: createPathFromRoute('/api/ProductManagement/ProductEnhancement/DocumentOrder'),
    body: params,
    headers: getDefaultHeaders(),
  });
  return serverResponse;
}
export default class ProductEnhancementDocument
  extends Model<Model.IProductEnhancementDocument>({ icon: 'fas-cloud' })
  implements Model.Instance<Model.IProductEnhancementDocument>
{
  id: number;
  orderNumber: number;
  productEnhancementId: number;
  mediaFileId: number;
  mediaFile: MediaFile;
  formFile?: File;
  documentCategory: Model.Enum.EngineType;
  productEnhancementAdditionalFields: Array<ProductEnhancementAdditionalField>;
  productEnhancementDealerDocuments: Array<ProductEnhancementDealerDocument>;
  languageIsoCode?: Locale.Languages = Language.english;

  constructor(
    data: Partial<Model.IProductEnhancementDocument> | Utils.FormData<Model.IProductEnhancementDocument>,
    language?: Locale.Languages
  ) {
    super(
      {
        mediaFile: MediaFile,
        productEnhancementAdditionalFields: [ProductEnhancementAdditionalField],
        productEnhancementDealerDocuments: [ProductEnhancementDealerDocument],
      },
      language
    );
    this.update(data);
  }
  get displayInfo(): Model.DisplayInfo {
    return {
      id: this.id,
      title: this.mediaFile?.name || this.id.toString(),
    };
  }
  static async find(params: { id: number }) {
    const { payload, ...rest } = await productEnhancementDocumentFindApi(params);
    return {
      ...rest,
      payload: payload ? new ProductEnhancementDocument(payload) : null,
    };
  }
  async save() {
    return await productEnhancementDocumentSaveApi(this);
  }
  async updateAllFields() {
    return await productEnhancementDocumentSaveApi({
      productEnhancementId: this.productEnhancementId,
      productEnhancementAdditionalFields: this.productEnhancementAdditionalFields,
    });
  }
  async delete() {
    return await productEnhancementDocumentDeleteApi({
      id: this.id,
      documentId: this.mediaFileId,
    });
  }
}
